import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import Draggable from 'react-draggable'

import { Box } from '@mui/material'

import FileViewer from './FileViewer'

export type FileViewerData = {
  contentType: string
  fileName: string
  fileBlob: Blob
}

export type FileViewerInternalData = {
  contentType: string
  fileName: string
  fileObjectURL: string
}

export interface FileViewerRef {
  isFileVisible: boolean
  setIsFileVisible: (newValue: boolean) => void
  setData: (data: FileViewerData) => void
}

export const FILE_VIEWER_DRAG_HANDLE_CLASS = 'drag-handle'

type FileViewerContainerProps = {
  onUndock: () => void
  onDock: () => void
  onClose: () => void
}

/**
 * The file viewer UI that can be opened from anywhere in the app, with the
 * file content provided by the caller.
 */
const FileViewerContainer = forwardRef(
  ({ onUndock, onDock, onClose }: FileViewerContainerProps, fileViewerRef) => {
    const isFirstRender = useRef(true)
    const [isFileVisible, setIsFileVisible] = useState<boolean>(false)
    const [data, setData] = useState<FileViewerInternalData | null>(null)
    const [isDocked, setIsDocked] = useState<boolean>(true)

    // When the file visibility changes to visible we need to tell the parent
    // component whether or not we're docked or not. We also tell the parent
    // when the docking state changes.
    useEffect(() => {
      isFileVisible && isDocked ? onDock() : onUndock()
    }, [isFileVisible, isDocked, onDock, onUndock])

    // If the file visibility changes (not note on the first mount) then we
    // tell the parent if the file viewer is now closed. We don't do this on
    // the first mount because we'd otherwise get a double render, as the other
    // useEffect has already fired.
    useEffect(() => {
      if (isFirstRender.current) {
        isFirstRender.current = false
        return
      }

      if (!isFileVisible) {
        setData(null)
        onClose()
      }

      return () => {
        if (data?.fileObjectURL) {
          URL.revokeObjectURL(data.fileObjectURL)
        }
      }
    }, [isFileVisible, onClose, data])

    // The FileViewerContainer component is rendered all the time and wraps
    // the whole app content. The fileViewerRef is passed in to the container
    // once and doesn't change after that. This means that we don't get any
    // re-renders of this component when code in the app want's to open the
    // file viewer. Instead the FileViewer component gets re-rendered when
    // the caller changes the state of the file viewer by calling one of the
    // hook methods we expose on the ref.
    useImperativeHandle(fileViewerRef, () => ({
      setIsFileVisible,
      setData: (newData: FileViewerData) => {
        // When a hook user sets data we convert it into the format needed
        // internally and then set the state. This triggers a re-render of
        // the FileViewer component with our converted data. This allows us
        // to fully manage the use of URL.createObjectURL, being able to call
        // the revokeObjectURL method when the component is unmounted. The
        // caller doesn't need to worry about this.

        // Release memory from any previous file object URL
        if (data?.fileObjectURL) {
          URL.revokeObjectURL(data.fileObjectURL)
        }

        // Set internal state, triggering a re-render of the FileViewer component
        setData({
          contentType: newData?.contentType ?? '',
          fileName: newData?.fileName ?? '',
          fileObjectURL: newData?.fileBlob
            ? URL.createObjectURL(newData.fileBlob)
            : '',
        })
      },
    }))

    const viewerDomRef = useRef(null)

    if (!isFileVisible) return null

    if (isDocked) {
      return (
        <FileViewer
          setIsFileVisible={setIsFileVisible}
          isDocked={isDocked}
          setIsDocked={setIsDocked}
          data={data}
          dragHandleClass={FILE_VIEWER_DRAG_HANDLE_CLASS}
        />
      )
    }

    return (
      <Draggable
        nodeRef={viewerDomRef}
        handle={`.${FILE_VIEWER_DRAG_HANDLE_CLASS}`}
        // don't allow dragging the viewer outside the viewport, otherwise
        // you can lose the handle for dragging and get it stuck
        bounds={'html'}
      >
        {/* This box is important - it is what gets props forwarded to it by
          the Draggable component, these props change on each drag event. We
          definitely don't want the main viewer component to be re-rendered
          on ever drag event! By containing our viewer inside this box we
          allow the box element to be re-rendered (which is just a div), and
          the viewer component within it has no changes to it's own props on
          each drag event, therefore it doesn't thrash on re-renders when the
          user is dragging it. */}
        <Box
          ref={viewerDomRef}
          sx={{
            // when undocked we want to float above the page content, this is
            // achieved by setting an absolute position, then the flexbox layout
            // flows the main page content to full width
            position: isDocked ? 'relative' : 'absolute',
            // necessary to ensure the file viewer is stacked above the page content
            // the top nav bar has a z-index of 1030 currently so we're stepping
            // above that
            zIndex: 2000,
          }}
        >
          <FileViewer
            setIsFileVisible={setIsFileVisible}
            isDocked={isDocked}
            setIsDocked={setIsDocked}
            data={data}
            dragHandleClass={FILE_VIEWER_DRAG_HANDLE_CLASS}
          />
        </Box>
      </Draggable>
    )
  }
)
FileViewerContainer.displayName = 'FileViewerContainer'

export default FileViewerContainer
